'use client';

import { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import Image from 'next/image';
import { leaders } from '@/app/data/leaders';
import Breadcrumbs from '@/components/common/Breadcrumbs';
import Icon from '@/components/ui/AppIcon';

interface ExecutiveBODSectionProps {
  className?: string;
}

/* LEADERS DATA */

const ExecutiveBODSection = ({ className = '' }: ExecutiveBODSectionProps) => {
  const [selectedLeader, setSelectedLeader] = useState(0);
  const [showQuickNav, setShowQuickNav] = useState(false);

  return (
    <section
      data-header="dark"
      className={`py-8 bg-gradient-to-r from-green-900 to-black bg-[length:200%_200%] animate-gradient-x text-white ${className}`}
    >
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Breadcrumbs className=" mt-8 mb-2 sm:mb-4" />

        <div className="text-center mt-2  mb-2 sm:mb-4">
          <div className="inline-flex items-center space-x-2 bg-white/10 px-6 py-1 sm:py-2 rounded-full mb-2 sm:mb-4">
            <Icon name="BriefcaseIcon" size={20} variant="solid" className="text-accent" />
            <span className="font-body text-xs sm:text-sm font-medium text-accent">
              Executive Team
            </span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 sm:mb-4">
            <span className="text-accent">Leadership</span> Excellence
          </h2>
          <p className="text-base sm:text-lg md:text-2xl text-white max-w-7xl mx-auto">
            Our executive team brings together world-class expertise from leading development
            finance institutions, combining institutional rigor with deep African market knowledge
          </p>

          <div className="text-center mt-2 lg:hidden">
            <button
              onClick={() => setShowQuickNav(true)}
              className="group inline-flex items-center justify-center gap-2 sm:gap-3 rounded-md border border-accent bg-accent px-6 py-1 sm:px-10 sm:py-3 text-xs sm:text-sm md:text-base font-extrabold uppercase tracking-widest text-white transition-all duration-300 hover:bg-transparent hover:text-accent hover:shadow-accent/30"
            >
              <span>See Executives' Profiles</span>
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-gray-800/70 rounded-3xl overflow-hidden shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Profile */}
            <div className="p-4 sm:p-8 md:p-12 max-w-3xl mx-auto">
              <div className="flex items-center mb-6">
                <div className="relative h-24 mb-4 gap-2 sm:h-28 md:h-32 aspect-square">
                  {/* Image clipper */}
                  <div className="relative w-full h-full rounded-2xl overflow-hidden">
                    <Image
                      src={leaders[selectedLeader].image}
                      alt={leaders[selectedLeader].name}
                      fill
                      className="object-cover"
                      sizes="128px"
                    />
                  </div>

                  {/* Badge */}
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-accent rounded-full flex items-center justify-center shadow-md">
                    <Icon name="TrophyIcon" size={16} color="#FFFFFF" />
                  </div>
                </div>

                <div className="mt-4 sm:mt-0 sm:ml-6 text-center gap-2 sm:text-left">
                  <h3 className="text-lg sm:text-3xl font-bold  text-accent">
                    {leaders[selectedLeader].name}
                  </h3>
                  <p className="text-sm sm:text-xl text-white font-medium">
                    {leaders[selectedLeader].position}
                  </p>
                  {/* <p className="hidden lg:block text-white">{leaders[selectedLeader].background}</p> */}
                </div>
              </div>

              <p className="text-white mb-6 text-sm sm:text-base leading-relaxed">
                {leaders[selectedLeader].bio}
              </p>
              {/* <div className="mb-4">
                <h4 className="text-sm sm:text-xl  font-body  font-semibold text-white/80 mb-3  tracking-wide">
                  Background
                </h4>
                <div className="flex items-start space-x-2 mb-2">
                  <Icon
                    name="AcademicCapIcon"
                    size={16}
                    className="text-accent flex-shrink-0 mt-0.5"
                  />
                  <span className="text-white mb-6 text-sm sm:text-base leading-relaxed">
                    {leaders[selectedLeader].background}
                  </span>
                </div>
              </div> */}
              <div className="mb-4">
                <h4 className="text-sm sm:text-xl  font-body  font-semibold text-white/80 mb-3  tracking-wide">
                  Education
                </h4>
                <div className="flex items-start space-x-2 mb-2">
                  <Icon
                    name="AcademicCapIcon"
                    size={16}
                    className="text-accent flex-shrink-0 mt-0.5"
                  />
                  <span className="text-sm text-white">{leaders[selectedLeader].education}</span>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="text-sm sm:text-xl font-body  font-semibold text-white/80 mb-3  tracking-wide">
                  Previous Roles:
                </h4>
                {leaders[selectedLeader].previousRoles.map((role, idx) => (
                  <div key={idx} className="flex items-start space-x-2 mb-1">
                    <Icon name="CheckCircleIcon" size={14} className="text-accent  mr-2 mt-1" />
                    <span className="text-sm text-white">{role}</span>
                  </div>
                ))}
              </div>
              {/* Core Expertise */}
              <div className="mb-6">
                <h4 className="text-sm sm:text-xl font-body  font-semibold text-white/80 mb-3  tracking-wide">
                  Core Expertise
                </h4>
                <div className="flex flex-wrap gap-2 sm:gap-3 mb-6">
                  {leaders[selectedLeader].expertise.map((skill, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 sm:px-4 sm:py-2 bg-green-50 text-green-600 text-xs rounded-full border border-green-100"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Key Achievements */}
              <div>
                <h4 className="text-sm sm:text-xl font-semibold font-body   text-white/80 mb-3  tracking-wide">
                  Key Achievements
                </h4>

                <ul className="space-y-2 mb-6">
                  {leaders[selectedLeader].achievements.map((a, i) => (
                    <li key={i} className="flex items-start">
                      <Icon name="CheckCircleIcon" size={16} className="text-accent  mr-2 mt-1" />
                      <span className="text-sm text-white">{a}</span>
                    </li>
                  ))}
                </ul>

                {leaders[selectedLeader].linkedin && (
                  <a
                    href={leaders[selectedLeader].linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn profile"
                    className="inline-flex items-center gap-2 text-[#0A66C2] hover:opacity-80 transition"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#0A66C2]">
                      <path d="M20.45 20.45h-3.55v-5.4c0-1.3-.03-2.97-1.81-2.97-1.81 0-2.09 1.41-2.09 2.87v5.5H9.4V9h3.4v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.26 2.37 4.26 5.46v6.28z" />
                    </svg>
                    <span className="text-sm font-medium">View LinkedIn profile</span>
                  </a>
                )}
              </div>
            </div>

            {/* Selector */}
            <div className="bg-black/10 hidden lg:block backdrop-blur-xl p-8 md:p-12 rounded-3xl">
              <h4 className="text-xl font-body text-white mb-8">Leadership Team</h4>
              <div className="space-y-4">
                {leaders.map((leader, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedLeader(index)}
                    className={`w-full p-4 rounded-xl text-left ${
                      selectedLeader === index ? 'bg-white/20' : 'bg-white/10 hover:bg-white/15'
                    }`}
                  >
                    <div className="flex items-center">
                      <Image
                        src={leader.image}
                        alt={leader.name}
                        width={48}
                        height={48}
                        className="rounded-lg mr-4 object-cover"
                      />
                      <div>
                        <div className="text-white font-medium">{leader.name}</div>
                        <div className="text-accent font-medium text-sm">{leader.position}</div>
                      </div>
                      {selectedLeader === index && (
                        <Icon name="ChevronRight" size={20} className="text-secondary ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* QUICK NAV - MOBILE ONLY */}
      <aside
        className={`
    fixed z-[9998] transition-all duration-500 bg-white border border-green-600
    left-0 right-0 bottom-0
    rounded-t-3xl
    block lg:hidden
    ${showQuickNav ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full pointer-events-none'}
  `}
      >
        <nav className="relative w-full">
          <div className="relative p-4 sm:p-6 w-full">
            {/* MOBILE DRAG HANDLE */}
            <div className="flex justify-center mb-3">
              <div className="h-1.5 w-12 rounded-full bg-gray-300" />
            </div>

            {/* HEADER */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-body font-semibold text-green-700">
                Select Profile To View Bio
              </h3>

              {/* Close Button */}
              <button
                onClick={() => setShowQuickNav(false)}
                className="px-3 py-1 text-sm rounded-md text-gray-600 hover:bg-green-50 hover:text-green-600"
              >
                Close
              </button>
            </div>

            {/* LEADER LINKS */}
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
              {leaders.map((leader, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedLeader(index);
                    setShowQuickNav(false); // close after selection
                  }}
                  className={`
              w-full flex items-center gap-3 p-3 rounded-lg transition-all
              ${
                selectedLeader === index
                  ? 'bg-white/5 text-black font-body'
                  : 'bg-white/90 text-gray-700 hover:bg-green-50 hover:text-green-600'
              }
            `}
                >
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white">
                    <Image
                      src={leader.image}
                      alt={leader.name}
                      width={40}
                      height={40}
                      className="object-cover"
                    />
                  </div>
                  <span className="text-sm font-medium truncate">{leader.name}</span>
                </button>
              ))}
            </div>
          </div>
        </nav>
      </aside>
    </section>
  );
};
export default ExecutiveBODSection;
